import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import heroVideoMobileSrc from "./assets/hero/hero-video.mobile.mp4";
import heroVideoSrc from "./assets/hero/hero-video.optimized.mp4";
import heroVideoPosterSrc from "./assets/hero/hero-video.poster.jpg";
import HeroVideoSection from "./components/hero/HeroVideoSection";
import BiodiversityCreditsSection from "./components/biodiversity-credits/BiodiversityCreditsSection";
import ProjectCarouselSection from "./components/project-carousel/ProjectCarouselSection";
import RegistryImportanceSection from "./components/registry-importance/RegistryImportanceSection";
import ImpactBannerSection from "./components/impact-banner/ImpactBannerSection";
import RegistryFooter from "./components/footer/RegistryFooter";
import IframeMap from "./components/map";
import Ledger from "./components/ledger";
import styles from "./App.module.css";

const mapIframeUrl =
  import.meta.env.VITE_MAP_IFRAME_URL ??
  (import.meta.env.MODE === "production"
    ? "https://biodiversity-map-production.web.app/"
    : "https://biodiversity-map-development.web.app/");

type SectionErrorStateProps = {
  title: string;
  description: string;
};

function SectionErrorState({ title, description }: SectionErrorStateProps) {
  return (
    <div className={styles.errorState} role="alert">
      <p className={styles.errorTitle}>{title}</p>
      <p className={styles.errorDescription}>{description}</p>
    </div>
  );
}

function MapErrorFallback() {
  return (
    <SectionErrorState
      title="Unable to load the biodiversity map."
      description="Please check your connection and try again."
    />
  );
}

function LedgerErrorFallback() {
  return (
    <SectionErrorState
      title="Unable to load registry data."
      description="Please refresh the page or try again in a moment."
    />
  );
}

function App() {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <main>
        <HeroVideoSection
          videoSrc={heroVideoSrc}
          mobileVideoSrc={heroVideoMobileSrc}
          posterSrc={heroVideoPosterSrc}
          heading={"The Voluntary\nBiodiversity\nCredit Registry"}
          description="A transparent, scientifically-verified platform recording every voluntary biodiversity credit owned and retired."
          ctaLabel="View the registry"
          mapTargetId="registry-ledger"
        />
        <BiodiversityCreditsSection />
        <ProjectCarouselSection />
        <RegistryImportanceSection />
        <section
          id="registry-ledger"
          aria-label="Registry ledger"
          className={`${styles.contentSection} ${styles.ledgerSection}`}
        >
          <h3
            id="registry-ledger-heading"
            className={`${styles.sectionHeading} ${styles.sectionHeadingStrong}`}
          >
            Retired voluntary biodiversity credits
          </h3>
          <ErrorBoundary FallbackComponent={LedgerErrorFallback}>
            <Ledger tableLabelledById="registry-ledger-heading" />
          </ErrorBoundary>
        </section>
        <section
          id="registry-map"
          aria-label="Registry map"
          className={`${styles.contentSection} ${styles.mapSection}`}
        >
          <h3 className={styles.sectionHeading}>
            VIEW BIODIVERSITY CREDIT PROJECTS
          </h3>
          <ErrorBoundary FallbackComponent={MapErrorFallback}>
            <IframeMap src={mapIframeUrl} title="Universal Biodiversity Map" />
          </ErrorBoundary>
        </section>
        <ImpactBannerSection imageSrc="/design/impact/vbc-registry-footer.png" />
        <RegistryFooter
          cookiePolicyHref="/cookie-policy"
          privacyPolicyHref="/privacy-policy"
          termsHref="/terms-and-conditions"
        />
      </main>
    </QueryClientProvider>
  );
}

export default App;
